import { useState, useEffect, useMemo } from 'react'
import { Button, Input, Badge, Card, Spinner, Modal } from '../components/ui'
import { useTypedNavigate, ROUTES } from '../lib/routing'
import { programsAPI, type Program } from '../lib/services'

type SortField = 'name' | 'created_at' | 'status' | 'submissionsCount' | 'is_starred'
type SortDirection = 'asc' | 'desc'

const Programs = () => {
  const navigate = useTypedNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | Program['status']>('all')
  const [programs, setPrograms] = useState<Program[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; program: Program | null }>({
    open: false,
    program: null,
  })
  const [deleting, setDeleting] = useState(false)

  // Fetch programs on component mount
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await programsAPI.list()
        setPrograms(data)
      } catch (err) {
        console.error('Error fetching programs:', err)
        setError(err instanceof Error ? err.message : 'Failed to load programs')
      } finally {
        setLoading(false)
      }
    }

    fetchPrograms()
  }, [])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'name' ? 'asc' : 'desc')
    }
  }

  const handleStar = async (e: React.MouseEvent, program: Program) => {
    e.stopPropagation()
    try {
      if (program.is_starred) {
        await programsAPI.unstar(program.id)
      } else {
        await programsAPI.star(program.id)
      }
      setPrograms((prev) =>
        prev.map((p) => (p.id === program.id ? { ...p, is_starred: !p.is_starred } : p))
      )
    } catch (err) {
      console.error('Error toggling star:', err)
    }
  }

  const handleDeleteClick = (e: React.MouseEvent, program: Program) => {
    e.stopPropagation()
    setDeleteModal({ open: true, program })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.program) return
    try {
      setDeleting(true)
      await programsAPI.delete(deleteModal.program.id)
      setPrograms((prev) => prev.filter((p) => p.id !== deleteModal.program!.id))
      setDeleteModal({ open: false, program: null })
    } catch (err) {
      console.error('Error deleting program:', err)
    } finally {
      setDeleting(false)
    }
  }

  const filteredAndSortedPrograms = useMemo(() => {
    let result = programs.filter((program) => {
      const matchesSearch =
        program.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        program.rfpNumber.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesStatus = statusFilter === 'all' || program.status === statusFilter
      return matchesSearch && matchesStatus
    })

    // Sort
    result = [...result].sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
        case 'submissionsCount':
          comparison = (a.submissionsCount || 0) - (b.submissionsCount || 0)
          break
        case 'is_starred':
          comparison = (a.is_starred ? 1 : 0) - (b.is_starred ? 1 : 0)
          break
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })

    // Always show starred items first when not explicitly sorting by starred
    if (sortField !== 'is_starred') {
      result = [...result].sort((a, b) => {
        if (a.is_starred && !b.is_starred) return -1
        if (!a.is_starred && b.is_starred) return 1
        return 0
      })
    }

    return result
  }, [programs, searchQuery, statusFilter, sortField, sortDirection])

  const SortableHeader = ({
    field,
    children,
    className = '',
  }: {
    field: SortField
    children: React.ReactNode
    className?: string
  }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {sortDirection === 'asc' ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            )}
          </svg>
        )}
      </div>
    </th>
  )

  // Show loading state
  if (loading) {
    return (
      <div className="h-full overflow-auto bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-96">
            <Spinner size="lg" />
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="h-full overflow-auto bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="p-8 text-center">
            <div className="text-destructive mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Error Loading Programs</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Reload Page</Button>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">Programs</h1>
          <p className="text-base text-muted-foreground mt-2">
            Manage acquisition programs and RFPs
          </p>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-4 mb-6 shadow-xs">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1 w-full min-w-0">
              <Input
                placeholder="Search programs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setStatusFilter('all')}
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                size="default"
              >
                All
              </Button>
              <Button
                onClick={() => setStatusFilter('active')}
                variant={statusFilter === 'active' ? 'default' : 'outline'}
                size="default"
              >
                Active
              </Button>
              <Button
                onClick={() => setStatusFilter('planning')}
                variant={statusFilter === 'planning' ? 'default' : 'outline'}
                size="default"
              >
                Planning
              </Button>
              <Button
                onClick={() => setStatusFilter('evaluation')}
                variant={statusFilter === 'evaluation' ? 'default' : 'outline'}
                size="default"
              >
                Evaluation
              </Button>
              <Button
                onClick={() => setStatusFilter('closed')}
                variant={statusFilter === 'closed' ? 'default' : 'outline'}
                size="default"
              >
                Closed
              </Button>
            </div>
          </div>
        </div>

        {/* Programs Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="w-12 px-4 py-3"></th>
                  <SortableHeader field="name" className="min-w-[200px]">
                    Program
                  </SortableHeader>
                  <SortableHeader field="status">Status</SortableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Category
                  </th>
                  <SortableHeader field="created_at">Created</SortableHeader>
                  <SortableHeader field="submissionsCount">Submissions</SortableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Reviews
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="w-12 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredAndSortedPrograms.map((program) => (
                  <tr
                    key={program.id}
                    className="hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() =>
                      // Land on the program overview (the module workflow graph),
                      // not the Solutions page (DEV-732).
                      navigate(ROUTES.pm.programs.children!.detail, {
                        programId: program.id.toString(),
                      })
                    }
                  >
                    <td className="px-4 py-4">
                      <button
                        onClick={(e) => handleStar(e, program)}
                        className="text-muted-foreground hover:text-warning-foreground transition-colors"
                        title={program.is_starred ? 'Unpin program' : 'Pin program'}
                      >
                        <svg
                          className="w-5 h-5"
                          fill={program.is_starred ? 'currentColor' : 'none'}
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                          />
                        </svg>
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <span className="text-xs text-muted-foreground">{program.rfpNumber}</span>
                        <p className="font-medium text-foreground">{program.name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge
                        variant={
                          program.status === 'active'
                            ? 'approved'
                            : program.status === 'planning'
                              ? 'pending'
                              : 'default'
                        }
                      >
                        {program.status.charAt(0).toUpperCase() + program.status.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">{program.category}</td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">{program.submittedAt}</td>
                    <td className="px-4 py-4 text-sm text-foreground font-medium">
                      {program.submissionsCount}
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {program.reviewsCompleted}/{program.reviewsTotal}
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {program.primaryContact}
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={(e) => handleDeleteClick(e, program)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        title="Delete program"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAndSortedPrograms.length === 0 && (
            <div className="p-12 text-center">
              <p className="text-muted-foreground">No programs found</p>
            </div>
          )}
        </Card>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteModal.open}
          onClose={() => setDeleteModal({ open: false, program: null })}
          title="Delete Program"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">{deleteModal.program?.name}</span>? This
              action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteModal({ open: false, program: null })}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Program'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  )
}

export default Programs
